// 남양주 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 (통합돌봄과 노영광 ☎8660 /
// 돌봄지원팀장 박승현 ☎6741) PDF 요구사항을 담는 additive 위젯 컨테이너.
// 기존 dashboard-region.tsx는 손대지 않고 하단에 조건부로 얹는다.
//
// 활성 조건은 서버가 결정: region.code === 'R019' AND Setting[:general,:namyangju_v1_enabled] 미비활성.
// 서버가 { enabled: false } 반환 시 아무것도 렌더하지 않아 R018(김해) 등에는 영향 없음.
//
// 위젯 배치 순서(브리핑 우선):
//   ① A1  ServiceFlow      — 12단계 워크플로우 (전체 흐름 조망)
//   ② B4  ServiceCatalog   — 어떠한 서비스가 있는지 (시민 관점)
//   ③ C   ClosureBoard     — 종결 처리 요약 (서비스 성과)
//   ④ B8  MonitoringPulse  — 서비스가 잘되었는지 확인 (품질 검증)
import * as React from "react";
import { ServiceFlow } from "./widgets/ServiceFlow";
import { ServiceCatalog } from "./widgets/ServiceCatalog";
import { MonitoringPulse } from "./widgets/MonitoringPulse";
import { ClosureBoard } from "./widgets/ClosureBoard";

interface RegionInfo {
  id: number;
  code: string;
  name: string;
  area?: string;
}

interface Props {
  region: RegionInfo;
}

// 단일 fetch helper — 에러 시 null 반환. 위젯별로 개별 실패해도 다른 위젯에는 영향 없게.
async function fetchWidget(url: string): Promise<any | null> {
  try {
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    if (!r.ok) return null;
    const body = await r.json();
    if (body?.enabled === false) return null;
    return body;
  } catch {
    return null;
  }
}

// 위젯 카탈로그 — 앵커 네비게이션과 렌더 순서의 단일 원천.
// 브리핑에서 강조점을 즉시 식별할 수 있도록 번호 · 이름 · 강조점을 명시.
const WIDGETS = [
  { id: "nyj-w1", num: "①", key: "flow",       title: "12단계 흐름",       highlight: "전체 흐름 조망",       color: "orange" },
  { id: "nyj-w2", num: "②", key: "catalog",    title: "서비스 카탈로그",   highlight: "어떠한 서비스가 있는지", color: "sky"    },
  { id: "nyj-w3", num: "③", key: "closure",    title: "종결 보드",         highlight: "종결 처리 · 성과",     color: "green"  },
  { id: "nyj-w4", num: "④", key: "monitoring", title: "품질 · 모니터링",   highlight: "잘되었는지 확인",      color: "rose"   },
] as const;

export function NamyangjuSupplement({ region }: Props) {
  const [flow, setFlow] = React.useState<any | null>(null);
  const [catalog, setCatalog] = React.useState<any | null>(null);
  const [monitoring, setMonitoring] = React.useState<any | null>(null);
  const [closure, setClosure] = React.useState<any | null>(null);

  React.useEffect(() => {
    if (region.code !== "R019") {
      setFlow(null); setCatalog(null); setMonitoring(null); setClosure(null);
      return;
    }
    const q = new URLSearchParams({ code: region.code }).toString();
    fetchWidget(`/api/v1/dashboard/region/service_flow?${q}`).then(setFlow);
    fetchWidget(`/api/v1/dashboard/region/service_catalog?${q}`).then(setCatalog);
    fetchWidget(`/api/v1/dashboard/region/monitoring_pulse?${q}`).then(setMonitoring);
    fetchWidget(`/api/v1/dashboard/region/closure_board?${q}`).then(setClosure);
  }, [region.code]);

  const anyEnabled = flow || catalog || monitoring || closure;
  if (!anyEnabled) return null;

  // 앵커로 부드럽게 스크롤 — 사용자가 위젯 이름을 클릭해 즉시 이동 가능.
  const jumpTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
      {/* 남양주 전용 섹션 헤더 + 위젯 점프 메뉴 */}
      <div style={{
        padding: "12px 16px",
        background: "linear-gradient(180deg, var(--surface) 0%, var(--bg-sub) 100%)",
        border: "1px solid var(--border-soft)",
        borderLeft: "4px solid var(--orange-500)",
        borderRadius: 10,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "var(--ink-1)", letterSpacing: 0.3 }}>
                남양주시 통합돌봄 · N-CARE
              </div>
              <span className="badge badge-orange" style={{ fontSize: 10, letterSpacing: 0.3 }}>PILOT</span>
            </div>
            <div className="muted" style={{ fontSize: 10.5, marginTop: 3 }}>
              「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 · 통합돌봄과 노영광 ☎8660 · 돌봄지원팀장 박승현 ☎6741
            </div>
          </div>
          {/* 위젯 점프 메뉴 — 4개 위젯 이름을 한 눈에 보여줌 */}
          <div role="navigation" aria-label="남양주 위젯 바로가기" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {WIDGETS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => jumpTo(w.id)}
                title={`${w.title} — ${w.highlight}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", borderRadius: 8,
                  border: `1px solid var(--${w.color}-500)`,
                  background: "var(--surface)",
                  color: "var(--ink-1)",
                  fontSize: 11.5, fontWeight: 700,
                  cursor: "pointer",
                  transition: "background .15s ease, color .15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `var(--${w.color}-50)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "var(--surface)";
                }}
              >
                <span style={{ color: `var(--${w.color}-600)`, fontWeight: 800 }}>{w.num}</span>
                <span>{w.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ① 12단계 흐름 */}
      {flow?.stages && (
        <div id="nyj-w1">
          <WidgetLabel num="①" title="12단계 흐름" highlight="전체 흐름 조망" color="orange" />
          <ServiceFlow data={flow}/>
        </div>
      )}

      {/* ② 서비스 카탈로그 + ③ 종결 보드 (2열) */}
      {(catalog?.groups || closure?.totals) && (
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14 }}>
          {catalog?.groups ? (
            <div id="nyj-w2">
              <WidgetLabel num="②" title="서비스 카탈로그" highlight="어떠한 서비스가 있는지" color="sky" />
              <ServiceCatalog data={catalog}/>
            </div>
          ) : <div/>}
          {closure?.totals ? (
            <div id="nyj-w3">
              <WidgetLabel num="③" title="종결 보드" highlight="종결 처리 · 성과" color="green" />
              <ClosureBoard data={closure}/>
            </div>
          ) : <div/>}
        </div>
      )}

      {/* ④ 서비스 품질 · 모니터링 */}
      {monitoring?.today && (
        <div id="nyj-w4">
          <WidgetLabel num="④" title="서비스 품질 · 모니터링" highlight="잘되었는지 확인" color="rose" />
          <MonitoringPulse data={monitoring}/>
        </div>
      )}
    </div>
  );
}

// 각 위젯 위에 붙는 번호+이름+강조점 라벨. 시선이 카드보다 먼저 라벨을 잡아
// 위젯 정체성이 즉시 식별되도록.
function WidgetLabel({ num, title, highlight, color }: { num: string; title: string; highlight: string; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, paddingLeft: 2 }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 6,
        background: `var(--${color}-50)`, color: `var(--${color}-600)`,
        fontSize: 13, fontWeight: 800,
      }}>{num}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color: "var(--ink-1)" }}>{title}</span>
      <span className="muted" style={{ fontSize: 11, fontWeight: 600 }}>· {highlight}</span>
    </div>
  );
}
